import {Input, Space, Table, Select, Divider, Button, Form, Tooltip, Modal, Spin, Tag} from 'antd';
import {
  InfoCircleOutlined,
  //HistoryOutlined,
  SettingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {useAppDispatch, useAppSelector} from '../../common/hooks';
import {useEffect, useState} from 'react';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import CustomColumnModal from './CustomColumnModal';
import {
  battteryDetails,
  liveBatteries,
  liveBatteriesConfig,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
} from './liveBatteriesSlice';
import DownloadCsvButton from './DownloadCsvButton';
import type {LIVE_BATTERIES, MAIN_FILTER, TABLE_COLUMN} from './liveBatteries.type';
import {authStationsList} from '../setup/station/stations/stationsSlice';
import {racksList} from '../setup/rack/racks/racksSlice';
import {useForm} from 'antd/es/form/Form';
import InfoModal from '../common/batteryDetailModal';
import {debounce} from 'lodash';
import {debounceDelay, sortbyFilter} from '../../common/constants';
import {formatDate} from '../../utils/formatDate';

const LiveBatteries = () => {
  const dispatch = useAppDispatch();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
  const state = useAppSelector((state) => state.liveBatteries);
  const stationOptions = useAppSelector((state) => state.stations.stations.pageData);
  const rackOptions = useAppSelector((state) => state.racks.racks.pageData);
  const [mainFilters, setMainFilters] = useState<MAIN_FILTER>({battery_type: 'all'});
  const [isRackDisable, setIsRackDisable] = useState(true);
  const [searchForm] = useForm();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    dispatch(liveBatteriesConfig())
      .unwrap()
      .then(() => {
        setLoaded(true);
        dispatch(authStationsList({is_active: true}));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    dispatch(
      liveBatteries({
        body: mainFilters,
        filters: {
          page_number: state.liveBatteriesList.page_number,
          page_size: state.liveBatteriesList.page_size,
          search: state.liveBatteriesList.search,
          sort_by: state.liveBatteriesList.sort_by,
          ascending: state.liveBatteriesList.ascending,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.liveBatteriesList.page_number,
    state.liveBatteriesList.page_size,
    state.liveBatteriesList.search,
    state.liveBatteriesList.sort_by,
    state.liveBatteriesList.ascending,
  ]);

  const handleSearch = (e: string) => {
    dispatch(searchBy(e));
  };

  const handleStationBouncing = debounce((e) => {
    dispatch(authStationsList({search: e, is_active: true}));
  }, debounceDelay);

  function getItem(x: TABLE_COLUMN) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    const temp: any = {title: x.pretty_name, dataIndex: x.key, width: 150};
    if (x.key === 'asset_id') {
      temp.fixed = 'left';
      temp.sorter = true;
    }
    if (x.key === 'name') {
      temp.fixed = 'left';
      temp.sorter = true;
    }
    if (x.key === 'soc') {
      temp.sorter = true;
    }
    if (x.key === 'soh') {
      temp.sorter = true;
    }
    if (x.key === 'rack_position') {
      temp.render = (x: number) => {
        return x !== null ? <>{x + 1}</> : null;
      };
    }
    if (x.key === 'status') {
      temp.render = (x: number) => {
        return x > 300 ? (
          <Tag color='green'>Charging</Tag>
        ) : x < -300 ? (
          <Tag color='red'>Discharging</Tag>
        ) : (
          <Tag color='blue'>Idle</Tag>
        );
      };
    }
    if (x.key === 'location') {
      temp.render = (x: number, y: any) => {
        return y.rack_position === null ? (
          <Tag color='red'>Field</Tag>
        ) : (
          <Tag color='yellow'>Premise</Tag>
        );
      };
    }

    if (x.key === 'cycles') {
      temp.sorter = true;
    }
    if (x.key === 'pack_voltage') {
      temp.sorter = true;
      temp.render = (x: number) => <>{(x / 1000).toFixed(2)}</>;
    }
    if (x.key === 'board_temp') {
      temp.sorter = true;
    }
    if (x.key === 'current') {
      temp.sorter = true;
      temp.render = (x: number) => <>{x > 300 || x < -300 ? (x / 1000).toFixed(2) : 0}</>;
    }
    if (x.key === 'temp_sensor') {
      temp.render = (x: number[]) => {
        const sum = x.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        const average = (sum / x.length).toFixed(1);
        return <>{average}</>;
      };
    }

    if (x.key === 'charging_fet') {
      temp.render = (x: boolean, y: any) => {
        return (
          <>{y.actualCharging ? <Tag color='success'> ON</Tag> : <Tag color='red'>OFF</Tag>}</>
        );
      };
    }
    if (x.key === 'discharging_fet') {
      temp.render = (x: boolean, y: any) => {
        return (
          <>{y.actualDischarging ? <Tag color='success'>ON</Tag> : <Tag color='red'>OFF</Tag>}</>
        );
      };
    }

    if (x.key === 'last_updated_battery') {
      temp.width = 200;
      temp.render = (x: number) => {
        return <>{formatDate(x)}</>;
      };
    }
    if (x.key === 'last_updated_location') {
      temp.width = 200;
      temp.render = (x: number) => {
        return <>{formatDate(x)}</>;
      };
    }
    if (x.key === 'latitude') {
      temp.width = 200;
    }
    if (x.key === 'longitude') {
      temp.width = 200;
    }
    if (x.key === 'model_info_id') {
      temp.width = 250;
    }

    if (x.key === 'fault_status') {
      temp.render = (x: string[]) => {
        if (x === undefined) {
          return null;
        } else {
          return (
            <Tooltip title='Click on info icon for detailed data'>
              {x.length > 0 ? <Tag color='red'>Fault</Tag> : <Tag color='success'>ALL OK</Tag>}
            </Tooltip>
          );
        }
      };
    }

    return temp;
  }

  const columns: ColumnsType<LIVE_BATTERIES> = loaded
    ? Array.from(state.config.employee_column_list.map((x) => getItem(x)))
    : [];

  columns.push({
    title: 'Actions',
    key: 'operation',
    fixed: 'right',
    width: 150,
    render: (_, record) => {
      return (
        <>
          <InfoCircleOutlined
            style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
            onClick={() => {
              dispatch(battteryDetails({asset_id: record.asset_id}))
                .unwrap()
                .then(() => {
                  setIsInfoModalOpen(true);
                });
            }}
          />
          {/* {userPermission.update_battery && (
          <HistoryOutlined
            style={{
              cursor: 'pointer',
              fontSize: '16px',
              color: '#1890FF',
              marginInline: '10px',
            }}
            onClick={() => {
              setIsHistoryModalOpen(true);
            }}
          />
          )} */}
        </>
      );
    },
  });

  const handleFormSubmit = (values: MAIN_FILTER) => {
    if (values.station_id === undefined || values.station_id?.length === 0) {
      delete values.station_id;
    }
    if (values.rack_id === undefined || values.station_id?.length === 0) {
      delete values.rack_id;
    }
    setMainFilters(values);
    dispatch(
      liveBatteries({
        body: values,
        filters: {
          page_number: state.liveBatteriesList.page_number,
          page_size: state.liveBatteriesList.page_size,
          search: state.liveBatteriesList.search,
          sort_by: state.liveBatteriesList.sort_by,
          ascending: state.liveBatteriesList.ascending,
        },
      }),
    );
  };

  return (
    <Spin spinning={state.isLoading} size={'large'} style={{marginTop: '40vh'}}>
      {loaded && (
        <>
          <div>
            <Form layout='inline' name='searchForm' form={searchForm} onFinish={handleFormSubmit}>
              <Form.Item name='station_id'>
                <Select
                  style={{width: '200px'}}
                  placeholder='Select station'
                  showArrow
                  //showSearch
                  filterOption={false}
                  allowClear
                  mode='multiple'
                  maxTagCount={1}
                  onSearch={(e) => {
                    if (e.length > 2) handleStationBouncing(e);
                  }}
                  onChange={(e) => {
                    searchForm.setFieldsValue({rack_id: undefined});

                    if (e.length < 1) {
                      searchForm.setFieldsValue({rack_id: undefined});
                      setIsRackDisable(true);
                    } else {
                      dispatch(racksList({station_id: e}));
                      setIsRackDisable(false);
                    }
                  }}
                >
                  {stationOptions.map((x, i) => (
                    <Select.Option key={i} value={x.id}>
                      {x.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name='rack_id'>
                <Select
                  placeholder='Select Cabinet'
                  style={{width: '200px'}}
                  mode='multiple'
                  maxTagCount={1}
                  allowClear
                  showArrow
                  disabled={isRackDisable}
                >
                  {rackOptions.map((x, i) => (
                    <Select.Option key={i} value={x.id}>
                      {x.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name='battery_type' initialValue={'all'}>
                <Select
                  style={{width: '200px'}}
                  options={[
                    {
                      value: 'all',
                      label: 'All',
                    },
                    {
                      value: 'premise',
                      label: 'Premise',
                    },

                    {
                      value: 'field',
                      label: 'Field',
                    },
                  ]}
                  showArrow
                />
              </Form.Item>
              <Button icon={<SearchOutlined />} type='primary' htmlType='submit'>
                Search
              </Button>
            </Form>
            <Divider style={{marginTop: '0px', marginBlock: '5px'}} />
            <div
              style={{
                margin: '10px 0px 10px 0px',
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '350px  1fr',
              }}
            >
              <Input.Search
                placeholder='Search'
                //defaultValue={}
                style={{borderRadius: '10px'}}
                allowClear
                onSearch={(e) => handleSearch(e)}
              />
              <div>
                <Space style={{float: 'right'}}>
                  <DownloadCsvButton filters={mainFilters} />
                  <Tooltip title='Customise Table' placement='left'>
                    <Button
                      icon={<SettingOutlined />}
                      onClick={() => setIsSettingModalOpen(true)}
                    />
                  </Tooltip>
                </Space>
              </div>
            </div>
            <Table
              rowKey='asset_id'
              columns={columns}
              pagination={
                {
                  total: state.liveBatteriesList.totalDataCount,
                  current: state.liveBatteriesList.page_number,
                  pageSize: state.liveBatteriesList.page_size,
                  showSizeChanger: true,
                } as TablePaginationConfig
              }
              //loading={state.isLoading}
              size='middle'
              dataSource={state.liveBatteriesList.pageData}
              scroll={{x: undefined, y: '69vh'}}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(pagination, _, {order, field}: any) => {
                if (
                  pagination.pageSize &&
                  pagination.pageSize !== state.liveBatteriesList.page_size
                ) {
                  dispatch(pageSizeChange(pagination.pageSize));
                }
                if (
                  pagination.current &&
                  pagination.current !== state.liveBatteriesList.page_number
                ) {
                  dispatch(pageNumberChange(pagination.current));
                }
                if (field) {
                  if (order === sortbyFilter.DESCEND) {
                    dispatch(sortBy({ascending: false, sortBy: field}));
                  } else {
                    dispatch(sortBy({ascending: true, sortBy: field}));
                  }
                }
              }}
            />
          </div>

          <CustomColumnModal isOpen={isSettingModalOpen} setIsOpen={setIsSettingModalOpen} />
        </>
      )}
      {isInfoModalOpen && <InfoModal isOpen={isInfoModalOpen} setIsOpen={setIsInfoModalOpen} />}
      <Modal
        title='History'
        open={isHistoryModalOpen}
        onCancel={() => {
          setIsHistoryModalOpen(false);
        }}
        footer={null}
        width={800}
      ></Modal>
    </Spin>
  );
};

export default LiveBatteries;
